import { useCallback, useEffect, useRef, useState } from 'react'

export type RehearsalClock = {
  elapsedSeconds: number
  isPlaying: boolean
  start: () => void
  stop: () => void
  reset: () => void
  replay: () => void
}

export function useRehearsalClock(durationSeconds: number): RehearsalClock {
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [isPlaying, setIsPlaying] = useState(false)
  const startTimestampRef = useRef<number | null>(null)
  const elapsedAtStartRef = useRef(0)

  useEffect(() => {
    if (!isPlaying) return

    let frame = 0

    const tick = (timestamp: number) => {
      if (startTimestampRef.current === null) {
        startTimestampRef.current = timestamp
      }

      const elapsed =
        elapsedAtStartRef.current + (timestamp - startTimestampRef.current) / 1000

      if (elapsed >= durationSeconds) {
        setElapsedSeconds(durationSeconds)
        setIsPlaying(false)
        startTimestampRef.current = null
        elapsedAtStartRef.current = durationSeconds
        return
      }

      setElapsedSeconds(elapsed)
      frame = requestAnimationFrame(tick)
    }

    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [durationSeconds, isPlaying])

  const start = useCallback(() => {
    if (elapsedAtStartRef.current >= durationSeconds) {
      elapsedAtStartRef.current = 0
      setElapsedSeconds(0)
    }
    startTimestampRef.current = null
    setIsPlaying(true)
  }, [durationSeconds])

  const stop = useCallback(() => {
    elapsedAtStartRef.current = elapsedSeconds
    startTimestampRef.current = null
    setIsPlaying(false)
  }, [elapsedSeconds])

  const reset = useCallback(() => {
    elapsedAtStartRef.current = 0
    startTimestampRef.current = null
    setElapsedSeconds(0)
    setIsPlaying(false)
  }, [])

  const replay = useCallback(() => {
    elapsedAtStartRef.current = 0
    startTimestampRef.current = null
    setElapsedSeconds(0)
    setIsPlaying(true)
  }, [])

  return { elapsedSeconds, isPlaying, start, stop, reset, replay }
}
